/**
 * Thin helpers that adapt gameplay events into PersistentStats mutations.
 *
 * Centralising these keeps the call sites in the game code minimal and makes
 * the mapping from gameplay event → achievement counter easy to audit.
 */

import type { Robot } from '../labyrinth/Robot';
import type { AchievementSystem } from './AchievementSystem';

/** Record a single loot item pickup. Increments band counters and totals,
 *  flags score-100 and boss-95+ drops, and tracks unique weapon discovery. */
export function trackLootPickup(
  robot: Robot, score: number, fromBoss: boolean, weaponId: string | null,
): void {
  const ach = robot.achievements;
  if (!ach) return;
  const s = ach.stats;
  s.lootTotalLooted++;
  if (score >= 96) s.lootLegendaryLooted++;
  else if (score >= 86) s.lootEpicLooted++;
  else if (score >= 61) s.lootRareLooted++;
  else if (score >= 31) s.lootUncommonLooted++;
  else s.lootBasicLooted++;
  if (score >= 100) s.score100Count++;
  if (fromBoss && score >= 95) s.bossDropAtLeast95Count++;
  if (weaponId) s.weaponsUniqueDiscovered.add(weaponId);
  ach.recheck();
}

/** Record a single swarm kill by tier. `tier` must be 1..5. */
export function trackSwarmKill(ach: AchievementSystem | null, tier: number): void {
  if (!ach) return;
  const s = ach.stats;
  s.totalKills++;
  s.swarmKills++;
  if (tier >= 1 && tier <= 5) {
    s.swarmKillsByTier[tier as 1 | 2 | 3 | 4 | 5]++;
  }
  // recheck is called by the caller in batch to avoid per-unit cost in a
  // 600-concurrent swarm; SwarmManager calls ach.recheck() once per frame.
}

/** Record a trader purchase. */
export function trackTraderBuy(
  ach: AchievementSystem | null, price: number, score: number,
): void {
  if (!ach) return;
  const s = ach.stats;
  s.tradesCompleted++;
  s.moneySpentAtTrader += price;
  if (score >= 90) s.legendaryBought++;
  ach.recheck();
}

/** Record a trader sale. */
export function trackTraderSell(
  ach: AchievementSystem | null, price: number,
): void {
  if (!ach) return;
  const s = ach.stats;
  s.tradesCompleted++;
  s.moneyFromSales += price;
  if (price > s.bestSinglePrice) s.bestSinglePrice = price;
  ach.recheck();
}

/** Record a forge completion (merge producing a higher-tier weapon). */
export function trackForge(
  ach: AchievementSystem | null,
  outputTier: number,
  outputCategory: 'ballistic' | 'energy' | 'rocket' | 'bomb' | 'melee',
): void {
  if (!ach) return;
  const s = ach.stats;
  s.forgeWeaponsForged++;
  if (outputTier >= 10) {
    s.forgeTier10Forged++;
    s.forgeTier10Categories.add(outputCategory);
  }
  ach.recheck();
}

/** Recompute equip-slot counters whenever the loadout changes. */
export function recomputeEquipStats(
  ach: AchievementSystem | null,
  equipped: Array<{ score: number } | null>,
): void {
  if (!ach) return;
  const s = ach.stats;
  let filled = 0, epic = 0, legendary = 0;
  for (const it of equipped) {
    if (!it) continue;
    filled++;
    if (it.score >= 86) epic++;
    if (it.score >= 96) legendary++;
  }
  if (filled > s.equipSlotsFilledMax) s.equipSlotsFilledMax = filled;
  if (epic > s.equipEpicSlotsMax) s.equipEpicSlotsMax = epic;
  if (legendary > s.equipLegendarySlotsMax) s.equipLegendarySlotsMax = legendary;
  ach.recheck();
}
